
import React, {
  useCallback,
  useEffect,
  useState,
} from 'react';

import stockService from '../services/stockService';
import StockTable from '../components/StockTable';
import '../styles/stock.css';

const DEFAULT_PAGINATION = {
  page: 1,
  limit: 10,
  total: 0,
  totalPages: 0,
};

const getErrorMessage = (error) =>
  error?.response?.data?.message ||
  error?.response?.data?.error ||
  error?.message ||
  'Something went wrong. Please try again.';

const StockPage = () => {
  const [stock, setStock] = useState([]);
  const [pagination, setPagination] = useState(
    DEFAULT_PAGINATION
  );

  const [searchInput, setSearchInput] = useState('');
  const [search, setSearch] = useState('');
  const [stockStatus, setStockStatus] = useState('');

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const [expandedProductId, setExpandedProductId] = useState(null);
  const [stockDetails, setStockDetails] = useState({});
  const [loadingProductId, setLoadingProductId] = useState(null);

  const loadStock = useCallback(async () => {
    setLoading(true);
    setError('');

    try {
      const result = await stockService.getStock({
        page: pagination.page,
        limit: pagination.limit,
        search,
        ...(stockStatus ? { stock_status: stockStatus } : {}),
      });

      setStock(result.stock);
      setPagination((current) => ({
        ...current,
        ...result.pagination,
      }));
    } catch (err) {
      setStock([]);
      setError(getErrorMessage(err));
    } finally {
      setLoading(false);
    }
  }, [
    pagination.page,
    pagination.limit,
    search,
    stockStatus,
  ]);

  useEffect(() => {
    loadStock();
  }, [loadStock]);

  const handleSearch = (event) => {
    event.preventDefault();

    setExpandedProductId(null);
    setSearch(searchInput.trim());
    setPagination((current) => ({
      ...current,
      page: 1,
    }));
  };

  const handleStatusChange = (event) => {
    setStockStatus(event.target.value);
    setExpandedProductId(null);
    setPagination((current) => ({
      ...current,
      page: 1,
    }));
  };

  const handleReset = () => {
    setSearchInput('');
    setSearch('');
    setStockStatus('');
    setExpandedProductId(null);
    setStockDetails({});
    setPagination((current) => ({
      ...current,
      page: 1,
    }));
  };

  const handleToggleDetails = async (productId) => {
    if (expandedProductId === productId) {
      setExpandedProductId(null);
      return;
    }

    setExpandedProductId(productId);

    // Reuse already-loaded batch information.
    if (stockDetails[productId]) {
      return;
    }

    setLoadingProductId(productId);

    setStockDetails((current) => {
      const next = { ...current };
      delete next[productId];
      return next;
    });

    try {
      const details =
        await stockService.getStockByProductId(productId);

      if (!details) {
        throw new Error('Stock details were not returned.');
      }

      setStockDetails((current) => ({
        ...current,
        [productId]: {
          ...details,
          batches: Array.isArray(details.batches)
            ? details.batches
            : [],
        },
      }));
    } catch (err) {
      setStockDetails((current) => ({
        ...current,
        [productId]: {
          error: getErrorMessage(err),
          batches: [],
        },
      }));
    } finally {
      setLoadingProductId(null);
    }
  };

  const goToPage = (nextPage) => {
    if (
      nextPage < 1 ||
      nextPage > pagination.totalPages ||
      nextPage === pagination.page
    ) {
      return;
    }

    setExpandedProductId(null);
    setPagination((current) => ({
      ...current,
      page: nextPage,
    }));
  };

  const totalQuantity = stock.reduce(
    (total, item) => total + Number(item.quantity || 0),
    0
  );

  const inStockCount = stock.filter(
    (item) => item.stock_status === 'in_stock'
  ).length;

  const lowStockCount = stock.filter(
    (item) => item.stock_status === 'low_stock'
  ).length;

  const outOfStockCount = stock.filter(
    (item) => item.stock_status === 'out_of_stock'
  ).length;

  return (
    <section className="stock-page">
      <header className="stock-page-header">
        <div>
          <h1>Stock Management</h1>
          <p>
            Monitor available quantities, low-stock products, and
            individual batch inventory.
          </p>
        </div>

        <button
          type="button"
          className="stock-refresh-button"
          onClick={loadStock}
          disabled={loading}
        >
          {loading ? 'Refreshing...' : 'Refresh Stock'}
        </button>
      </header>

      {error && (
        <div className="stock-alert" role="alert">
          <span>{error}</span>

          <button type="button" onClick={loadStock}>
            Retry
          </button>
        </div>
      )}

      <div className="stock-summary-grid">
        <article className="stock-summary-card">
          <span>Products Found</span>
          <strong>{pagination.total}</strong>
          <small>Matching inventory records</small>
        </article>

        <article className="stock-summary-card">
          <span>In Stock</span>
          <strong>{inStockCount}</strong>
          <small>Above the low-stock threshold</small>
        </article>

        <article className="stock-summary-card">
          <span>Low Stock</span>
          <strong>{lowStockCount}</strong>
          <small>At or below the alert threshold</small>
        </article>

        <article className="stock-summary-card">
          <span>Out of Stock</span>
          <strong>{outOfStockCount}</strong>
          <small>Zero available quantity</small>
        </article>
      </div>

      <section className="stock-content-card">
        <div className="stock-content-header">
          <div>
            <h2>Inventory</h2>
            <p>
              Showing {stock.length} products on this page.
              {' '}Quantity total: {totalQuantity.toLocaleString(
                'en-IN',
                { maximumFractionDigits: 3 }
              )}
            </p>
          </div>
        </div>

        <form className="stock-filters" onSubmit={handleSearch}>
          <div className="stock-search-field">
            <label htmlFor="stock-search">
              Search products
            </label>

            <input
              id="stock-search"
              type="search"
              value={searchInput}
              onChange={(event) =>
                setSearchInput(event.target.value)
              }
              placeholder="Product name, SKU, or brand"
              maxLength={100}
            />
          </div>

          <div className="stock-status-field">
            <label htmlFor="stock-status">
              Stock status
            </label>

            <select
              id="stock-status"
              value={stockStatus}
              onChange={handleStatusChange}
            >
              <option value="">All statuses</option>
              <option value="in_stock">In Stock</option>
              <option value="low_stock">Low Stock</option>
              <option value="out_of_stock">Out of Stock</option>
            </select>
          </div>

          <div className="stock-filter-actions">
            <button
              type="submit"
              className="stock-search-button"
              disabled={loading}
            >
              Search
            </button>

            <button
              type="button"
              className="stock-reset-button"
              onClick={handleReset}
              disabled={
                loading &&
                !search &&
                !searchInput &&
                !stockStatus
              }
            >
              Reset
            </button>
          </div>
        </form>

        <StockTable
          stock={stock}
          loading={loading}
          expandedProductId={expandedProductId}
          stockDetails={stockDetails}
          loadingProductId={loadingProductId}
          onToggleDetails={handleToggleDetails}
        />

        {!loading && pagination.totalPages > 0 && (
          <footer className="stock-pagination">
            <span>
              Page {pagination.page} of {pagination.totalPages}
              {' '}({pagination.total} products)
            </span>

            <div className="stock-pagination-actions">
              <button
                type="button"
                onClick={() => goToPage(pagination.page - 1)}
                disabled={pagination.page <= 1}
              >
                Previous
              </button>

              <button
                type="button"
                onClick={() => goToPage(pagination.page + 1)}
                disabled={
                  pagination.page >= pagination.totalPages
                }
              >
                Next
              </button>
            </div>
          </footer>
        )}
      </section>
    </section>
  );
};

export default StockPage;
